/**
 * Save for Later (Perkswallet) — the one implementation shared by every offer unit.
 *
 * SOU and MOU consume it through the `PerksWallet` component; COU consumes it
 * directly from its `data-ms-action="save-for-later"` handler, because a COU
 * template owns its own markup and only wants the eligibility rule, the button
 * copy, and the save call. Keeping all three here is what stops the two
 * presentations drifting on which offers are savable or which endpoint is hit.
 */
import { PERKSWALLET_API_URL, PERKSWALLET_APP_URL } from '../constants';
import { createDebugLogger } from './debug';
const debug = createDebugLogger('perksWallet');
/**
 * The PWAAS webhook path needs a publisher user id to address the save to;
 * without one the offer is saved through the hosted Perkswallet popup instead.
 */
export function usesPwaasSave(settings, userAttributes) {
    return Boolean(settings.enable_pwaas_webhook && userAttributes.pub_user_id);
}
/** Account-level setting enabled AND this offer opted in AND the CTA not suppressed. */
export function isPerksWalletEligible(offer, settings, userAttributes) {
    const enabled = (usesPwaasSave(settings, userAttributes) || settings.enable_perkswallet) &&
        offer.campaign.perkswallet_enabled;
    return Boolean(enabled && settings.perkswallet_show_cta);
}
/** Publisher-configurable copy, with the same defaults SOU/MOU have always shown. */
export function perksWalletButtonText(settings, state) {
    if (state === 'saving') {
        return 'Saving offer...';
    }
    if (state === 'saved') {
        return settings.saved_offer_text || 'Saved to Perkswallet';
    }
    return settings.perkswallet_cta || 'Save to Perkswallet';
}
async function saveOfferViaPwaas(offer, userAttributes) {
    try {
        const url = new URL(offer.click_url);
        const params = new URLSearchParams(url.search);
        const baseUrl = PERKSWALLET_API_URL;
        const apiUrl = `${baseUrl}/api/publisher/user/${userAttributes.pub_user_id}/save-offer.json`;
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'pwaas-api-key': params.get('p_id') || '',
            },
            body: JSON.stringify({
                publisher_id: params.get('p_id'),
                campaign_id: params.get('c_id'),
                session_id: params.get('sess_id'),
                internal_name: offer.campaign.internal_name,
                advertiser_name: offer.advertiser_name,
                headline: offer.title,
                short_headline: offer.short_headline,
                positive_cta: offer.cta_yes,
                mini_text: offer.cta_no,
                description: offer.description,
                short_description: offer.short_description,
                platforms: offer.campaign.platforms,
                primary_creative: offer.image,
                offer_id: params.get('o_id'),
            }),
        });
        if (!response.ok) {
            return 'failed';
        }
        offer.alreadySaved = true;
        return 'saved';
    }
    catch (error) {
        debug.error('Error saving offer:', error);
        return 'failed';
    }
}
function saveOfferViaPerkswalletPopup(offer, settings) {
    const url = new URL(offer.click_url);
    const params = new URLSearchParams(url.search);
    let saveUrl = `${PERKSWALLET_APP_URL}/save-offer?o_id=${params.get('o_id')}&c_id=${params.get('c_id')}&sess_id=${params.get('sess_id')}&fp=${params.get('fp')}&p_id=${params.get('p_id')}`;
    if (typeof settings.show_perkswallet_extension === 'boolean') {
        saveUrl += `&showext=${settings.show_perkswallet_extension ? 1 : 0}`;
    }
    window.open(saveUrl, '_blank');
    return 'opened';
}
/**
 * Saves `offer`, picking the same mechanism SOU/MOU pick. `opened` (the popup
 * path) deliberately does not mark the offer saved — the save completes in the
 * other tab, so the button stays live exactly as it does on SOU/MOU today.
 *
 * `async` so a malformed `click_url` surfaces as a rejection rather than a
 * synchronous throw from a Promise-returning function. Nothing is awaited before
 * `window.open`, so the popup path still runs inside the click's user gesture.
 */
export async function savePerksWalletOffer(offer, settings, userAttributes) {
    if (usesPwaasSave(settings, userAttributes)) {
        return saveOfferViaPwaas(offer, userAttributes);
    }
    return saveOfferViaPerkswalletPopup(offer, settings);
}
